import * as React from 'react';
import {
  Dialog,
  DialogHeader,
  DialogTitle,
  DialogContent,
} from 'components/ui/dialog';
import { Button } from 'components/ui/button';


type ButtonProps = React.ComponentPropsWithoutRef<typeof Button>;

type OpenDialogParams = {
  title?: React.ReactNode;
  message?: React.ReactNode;
}

type Props = OpenDialogParams & {
  confirmationButtonProps?: ButtonProps;
  confirmationButtonVariant?: ButtonProps['variant'];
  confirmationButtonLabel?: React.ReactNode;
  cancelButtonProps?: ButtonProps;
  cancelButtonLabel?: React.ReactNode;
};

type ConfirmationResult = 'canceled' | 'confirmed';

export type ConfirmationDialogRef = {
  openDialog: (params?: OpenDialogParams) => Promise<ConfirmationResult>;
};

export const ConfirmationDialog = React.forwardRef<
  ConfirmationDialogRef,
  Props
>((props, ref) => {
  const {
    title: defaultTitle = '',
    message: defaultMessage = 'Tem certeza de que deseja continuar?',
    confirmationButtonProps,
    confirmationButtonVariant = 'destructive',
    confirmationButtonLabel,
    cancelButtonProps,
    cancelButtonLabel,
  } = props;
  const [title, setTitle] = React.useState<React.ReactNode>(defaultTitle);
  const [message, setMessage] = React.useState<React.ReactNode>(defaultMessage);
  const [open, setOpen] = React.useState(false);
  const resolver = React.useRef((_value: ConfirmationResult) => { });

  const handleConfirm = React.useCallback(() => {
    resolver.current('confirmed');
    setOpen(false);
  }, []);

  const handleCancel = React.useCallback(() => {
    resolver.current('canceled');
    setOpen(false);
  }, []);

  const openDialog = React.useCallback((params?: OpenDialogParams): Promise<ConfirmationResult> => {
    if (params?.title) {
      setTitle(params.title);
    }

    if (params?.message) {
      setMessage(params.message);
    }

    setOpen(true);

    return new Promise((resolve) => {
      resolver.current = resolve;
    });
  }, []);

  React.useImperativeHandle(ref, () => {
    return { openDialog };
  }, [openDialog]);

  return (
    <Dialog open={open} onOpenChange={handleCancel}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        {message}
        <div className={'flex mt-6 gap-4'}>
          <Button
            type={'button'}
            size={'sm'}
            variant={'outline'}
            {...cancelButtonProps}
            onClick={handleCancel}
          >
            {cancelButtonLabel || 'Cancelar'}
          </Button>
          <Button
            type={'button'}
            size={'sm'}
            variant={confirmationButtonVariant}
            {...confirmationButtonProps}
            onClick={handleConfirm}
          >
            {confirmationButtonLabel || 'Confirmar'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
});
ConfirmationDialog.displayName = 'ConfirmationDialog';
